/**
 * Shared error handling utilities for all Kestrix applications
 *
 * This service provides centralized error handling, user-friendly error messages,
 * and consistent error UI components across the application.
 */
import { useCallback } from 'react';

/**
 * Error types and their corresponding user-friendly messages
 */
export const ERROR_TYPES = {
  CORS_ERROR: 'CORS Error',
  SERVICE_UNAVAILABLE: 'Service Unavailable',
  NETWORK_ERROR: 'Network Error',
  AUTHENTICATION_ERROR: 'Authentication Error',
  MODEL_NOT_FOUND: 'Model Not Found',
  FILE_NOT_ACCESSIBLE: 'File Not Accessible',
  EXPORT_ERROR: 'Export Error',
  FILE_GENERATION_ERROR: 'File Generation Error',
  UNKNOWN_ERROR: 'Unknown Error'
};

/**
 * User-friendly error messages for different error types
 */
export const ERROR_MESSAGES = {
  [ERROR_TYPES.CORS_ERROR]: {
    title: 'Access Problem',
    message: 'We\'re having trouble connecting to our servers. Please try again in a moment.',
    troubleshooting: [
      'Try refreshing the page',
      'Check your internet connection',
      'Contact support if the problem continues'
    ]
  },
  [ERROR_TYPES.SERVICE_UNAVAILABLE]: {
    title: 'Service Temporarily Unavailable',
    message: 'Our servers are taking a quick break. Please try again in a few minutes.',
    troubleshooting: [
      'Wait a few minutes and try again',
      'Check if other users are experiencing the same issue',
      'Contact support if the problem persists'
    ]
  },
  [ERROR_TYPES.NETWORK_ERROR]: {
    title: 'Connection Problem',
    message: 'We can\'t reach our servers right now. Please check your internet connection.',
    troubleshooting: [
      'Check your internet connection',
      'Try refreshing the page',
      'Make sure you\'re not behind a firewall'
    ]
  },
  [ERROR_TYPES.AUTHENTICATION_ERROR]: {
    title: 'Login Required',
    message: 'Your session has expired. Please refresh the page to log in again.',
    troubleshooting: [
      'Refresh the page to log in again',
      'Make sure you\'re logged in',
      'Contact support if you keep getting logged out'
    ]
  },
  [ERROR_TYPES.MODEL_NOT_FOUND]: {
    title: '3D Model Not Available',
    message: 'We couldn\'t find the 3D model you\'re looking for.',
    troubleshooting: [
      'Try selecting a different property',
      'Check if the model should be available',
      'Contact support if this seems wrong'
    ]
  },
  [ERROR_TYPES.FILE_NOT_ACCESSIBLE]: {
    title: 'File Not Available',
    message: 'We can\'t access the file you need right now.',
    troubleshooting: [
      'Try again in a moment',
      'Check if the file should be available',
      'Contact support if the problem continues'
    ]
  },
  [ERROR_TYPES.EXPORT_ERROR]: {
    title: 'Export Didn\'t Work',
    message: 'We couldn\'t export your data right now. Please try again.',
    troubleshooting: [
      'Check your internet connection',
      'Try refreshing the page',
      'Contact support if the problem persists'
    ]
  },
  [ERROR_TYPES.FILE_GENERATION_ERROR]: {
    title: 'File Creation Problem',
    message: 'We couldn\'t create your download file. Please try again.',
    troubleshooting: [
      'Check your browser\'s download settings',
      'Try refreshing the page',
      'Contact support if the problem continues'
    ]
  },
  [ERROR_TYPES.UNKNOWN_ERROR]: {
    title: 'Something Went Wrong',
    message: 'We encountered an unexpected problem. Please try again.',
    troubleshooting: [
      'Try refreshing the page',
      'Check your internet connection',
      'Contact support with the error details'
    ]
  }
};

/**
 * Analyzes an error and determines its type
 * 
 * @param {Error} error - The error object to analyze
 * @returns {string} The error type
 */
export const analyzeError = (error) => {
  if (!error || !error.message) {
    return ERROR_TYPES.UNKNOWN_ERROR;
  }

  const message = error.message.toLowerCase();

  if (message.includes('cors') || message.includes('access-control-allow-origin')) {
    return ERROR_TYPES.CORS_ERROR;
  }
  
  if (message.includes('503') || message.includes('service unavailable')) {
    return ERROR_TYPES.SERVICE_UNAVAILABLE;
  }
  
  if (message.includes('failed to fetch') || message.includes('network error')) {
    return ERROR_TYPES.NETWORK_ERROR;
  }
  
  if (message.includes('401') || message.includes('unauthorized') || message.includes('authentication')) {
    return ERROR_TYPES.AUTHENTICATION_ERROR;
  }
  
  if (message.includes('model not found') || message.includes('no glb file')) {
    return ERROR_TYPES.MODEL_NOT_FOUND;
  }
  
  if (message.includes('not accessible') || message.includes('html instead of glb')) {
    return ERROR_TYPES.FILE_NOT_ACCESSIBLE;
  }
  
  // Export-specific error detection
  if (message.includes('export') || message.includes('download') || message.includes('csv')) {
    if (message.includes('file') || message.includes('blob') || message.includes('generation')) {
      return ERROR_TYPES.FILE_GENERATION_ERROR;
    }
    return ERROR_TYPES.EXPORT_ERROR;
  }

  return ERROR_TYPES.UNKNOWN_ERROR;
};

/**
 * Gets user-friendly error information for an error
 * 
 * @param {Error} error - The error object
 * @returns {Object} Error information with title, message, and troubleshooting steps
 */
export const getErrorInfo = (error) => {
  const errorType = analyzeError(error);
  return {
    type: errorType,
    ...ERROR_MESSAGES[errorType],
    originalError: error
  };
};

/**
 * Enhanced error handler for API calls
 * 
 * @param {Error} error - The original error
 * @param {string} operationName - Name of the operation that failed
 * @returns {Error} Enhanced error with user-friendly message
 */
export const handleApiError = (error, operationName = 'API call') => {
  const errorInfo = getErrorInfo(error);
  
  console.error(`${operationName} failed:`, error);
  console.error(`Error type: ${errorInfo.type}`);
  console.error(`User message: ${errorInfo.message}`);
  
  // Create enhanced error with user-friendly message
  const enhancedError = new Error(`${errorInfo.title}: ${errorInfo.message}`);
  enhancedError.type = errorInfo.type;
  enhancedError.troubleshooting = errorInfo.troubleshooting;
  enhancedError.originalError = error;
  
  return enhancedError;
};

/**
 * React component for displaying error messages
 * 
 * @param {Object} props - Component props
 * @param {Error} props.error - The error to display
 * @param {string} props.title - Optional custom title
 * @param {Function} props.onRetry - Optional retry callback
 * @returns {JSX.Element} Error display component
 */
export const ErrorDisplay = ({ error, title, onRetry }) => {
  const errorInfo = getErrorInfo(error);
  const displayTitle = title || errorInfo.title;
  
  return (
    <div style={{ textAlign: 'center', padding: '20px' }}>
      <h3>{displayTitle}</h3>
      <p>{errorInfo.message}</p>
      
      {errorInfo.troubleshooting && errorInfo.troubleshooting.length > 0 && (
        <div style={{ marginTop: '15px' }}>
          <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '10px' }}>
            Try these steps:
          </p>
          <ul style={{ textAlign: 'left', display: 'inline-block', fontSize: '0.8em', color: '#888' }}>
            {errorInfo.troubleshooting.map((step, index) => (
              <li key={index}>{step}</li>
            ))}
          </ul>
        </div>
      )}
      
      {onRetry && (
        <div style={{ marginTop: '20px' }}>
          <button 
            onClick={onRetry}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Try Again
          </button>
        </div>
      )}
      
      <div style={{ marginTop: '15px', fontSize: '0.7em', color: '#aaa' }}>
        <details>
          <summary>Technical Details</summary>
          <pre style={{ textAlign: 'left', marginTop: '10px', fontSize: '0.7em' }}>
            {error.originalError?.message || error.message}
          </pre>
        </details>
      </div>
    </div>
  );
};

/**
 * Hook for handling errors in React components
 * 
 * @param {Function} setError - Function to set error state
 * @returns {Object} Error handling utilities
 */
export const useErrorHandler = (setError) => {
  const handleError = useCallback((error, operationName) => {
    const enhancedError = handleApiError(error, operationName);
    setError(enhancedError);
  }, [setError]);

  const handleApiErrorSilently = useCallback((error, operationName) => {
    const enhancedError = handleApiError(error, operationName);
    console.warn(`${operationName} failed silently:`, enhancedError.message);
    return enhancedError;
  }, []);

  return {
    handleError,
    handleApiErrorSilently,
    ErrorDisplay
  };
};
